import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  colorForParse,
  getRaidProgressParse,
  getRaidProgressSummary,
} from 'app/frontend/base/config/column-definitions/character'
import {
  useApplicationDetails,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import { canCharacterHeal } from 'app/frontend/base/services/characterValues'
import { capitalize } from 'lodash'
import { Link, useParams } from 'react-router-dom'
import RecentReportsDialog from './RecentReportsDialog'

const RaidProgressGrid = ({
  progress,
  characterReference,
  reports,
  style,
  team,
}: {
  progress: wowaudit.CharacterDetails['currentRaidProgression']
  characterReference: wowaudit.CharacterReference
  reports: wowaudit.CharacterDetails['recentReports']
  style?: React.CSSProperties
  team?: wowaudit.Team
}) => {
  const { applicationId } = useParams()
  const { data: applicationDetails } = useApplicationDetails(
    team?.id,
    applicationId ? parseInt(applicationId) : undefined,
  )
  const {
    data: { currentSeason },
  } = usePageInfo()

  const { difficulty, element: raidProgress } = getRaidProgressSummary(progress)
  const { element: raidParses } = getRaidProgressParse(progress)

  const allEncounters = currentSeason.instances.flatMap(
    (instance) => instance.encounters,
  )

  if (difficulty === null) return null

  return (
    <div>
      <div className='flex items-center justify-between mb-2'>
        <div className='text-muted-foreground text-sm font-normal'>
          Raid progress -{' '}
          <span
            className={`text-quality-${
              {
                mythic: 'epic',
                heroic: 'rare',
                normal: 'uncommon',
                raid_finder: 'common',
              }[difficulty]
            } font-semibold`}
          >
            {capitalize(difficulty).replace('_', ' ')}
          </span>
        </div>

        <div className='flex items-center gap-2'>
          {reports && reports.length > 0 && (
            <RecentReportsDialog reports={reports ?? []} style={style} />
          )}

          <Badge
            variant='outline'
            className='flex items-center bg-background'
            style={style}
          >
            <span>{raidProgress}</span>
            <Link
              to={`${characterReference.wclUrl}?metric=${
                applicationDetails?.role === 'heal' &&
                canCharacterHeal(characterReference.classId)
                  ? 'hps'
                  : 'dps'
              }`}
              className='ml-2 border-l border-accent pl-2'
              target='_blank'
              rel='noreferrer'
            >
              {raidParses}
            </Link>
          </Badge>
        </div>
      </div>

      <div className='flex items-center gap-2 flex-wrap'>
        {allEncounters.map((encounter) => {
          const encounterProgress = progress[difficulty]?.[encounter.id]

          return (
            <div
              key={encounter.id}
              className={`flex items-center gap-2 bg-background border rounded-md pr-1 w-36 ${
                encounterProgress?.kills ? '' : ' opacity-30 grayscale'
              }`}
              style={style}
            >
              <img
                src={encounter.zoomUrl}
                alt={encounter.name}
                className='w-12 h-12 rounded-l-md'
              />
              <div className='flex flex-col min-w-0'>
                <span className='truncate'>{encounter.name}</span>
                <div>
                  {encounterProgress?.kills ? (
                    <Badge variant='outline' className='bg-muted h-4'>
                      <span>{encounterProgress?.kills}×</span>
                      <Tooltip>
                        <TooltipContent>Best parse</TooltipContent>
                        <TooltipTrigger>
                          <Link
                            to={`${characterReference.wclUrl}?metric=${
                              applicationDetails?.role === 'heal' &&
                              canCharacterHeal(characterReference.classId)
                                ? 'hps'
                                : 'dps'
                            }&boss=${encounter.wclId}`}
                            className={`ml-2 border-l border-accent pl-2 ${colorForParse(
                              encounterProgress?.parse ?? 0,
                            )}`}
                            target='_blank'
                            rel='noreferrer'
                          >
                            {encounterProgress?.parse || '-'}
                          </Link>
                        </TooltipTrigger>
                      </Tooltip>
                    </Badge>
                  ) : (
                    <span className='text-muted-foreground'>-</span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RaidProgressGrid
